import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, CloudUpload, FileText, FolderOpen, Star, Tag, Trash2, Download } from "lucide-react";
import { PremiumInput } from "../brand/PremiumInput";
import { PremiumSelect } from "../brand/PremiumSelect";
import { BlockerStrip } from "./BlockerStrip";
import { useChainStore } from "../../data/chainStore";
import { api } from "../../lib/api";

const TAGS = ["TA6", "TA10", "EWS1", "ID", "Proof of Funds", "Contract Pack", "Mortgage Offer", "Searches", "Management Pack"];

const tagTone = (t) => {
  const map = {
    TA6: "bg-indigo-50 text-indigo-700 border-indigo-200",
    TA10: "bg-indigo-50 text-indigo-700 border-indigo-200",
    EWS1: "bg-rose-50 text-rose-700 border-rose-200",
    ID: "bg-blue-50 text-blue-700 border-blue-200",
    "Proof of Funds": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Contract Pack": "bg-indigo-50 text-indigo-700 border-indigo-200",
    "Mortgage Offer": "bg-amber-50 text-amber-700 border-amber-200",
    Searches: "bg-slate-100 text-slate-700 border-slate-200",
    "Management Pack": "bg-rose-50 text-rose-700 border-rose-200",
  };
  return map[t] || map.Searches;
};

const fmtAgo = (ts) => {
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

export const DocumentHub = ({ deal, access }) => {
  const { addDocument, findContact, folders, favDocs, toggleFavDoc } = useChainStore();
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(null);
  const [tag, setTag] = useState("TA6");
  const [q, setQ] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [folder, setFolder] = useState("All");
  const inputRef = useRef(null);

  // iter-184 P1 — visibility-aware upload gating. Defaults to allowed
  // while access is still loading so org-owners never see a flash of
  // disabled state. The backend also enforces (POST /documents 403).
  const canUpload = access?.can_upload_documents !== false;

  const docs = useMemo(() => deal?.documents || [], [deal?.documents]);

  const filtered = useMemo(() => {
    return docs.filter((d) => {
      if (folder !== "All" && d.folder !== folder) return false;
      if (filterTag && d.tag !== filterTag) return false;
      if (q && !d.name.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [docs, q, filterTag, folder]);

  const folderCounts = useMemo(() => {
    const counts = { All: docs.length };
    folders.forEach((f) => { counts[f] = docs.filter((d) => d.folder === f).length; });
    return counts;
  }, [docs, folders]);

  // Detect missing-doc warnings (e.g. no Proof of Funds, no Contract Pack)
  const missing = useMemo(() => {
    const required = ["ID", "Proof of Funds", "Contract Pack", "Mortgage Offer"];
    return required.filter((tg) => !docs.some((d) => d.tag === tg));
  }, [docs]);

  // Live document-dependency report — drives the calm blocker strip.
  const [deps, setDeps] = useState(null);
  useEffect(() => {
    if (!deal?.id) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/api/deals/${deal.id}/document-dependencies`);
        if (alive) setDeps(data?.dependencies || null);
      } catch { /* noop — Mock deals (legacy chainStore) won't have backend rows */ }
    })();
    return () => { alive = false; };
  }, [deal?.id]);

  const handleFiles = (files) => {
    const file = files?.[0];
    if (!file) return;
    setProgress({ name: file.name, pct: 0 });
    let pct = 0;
    const tick = () => {
      pct = Math.min(pct + 8 + Math.random() * 14, 100);
      setProgress({ name: file.name, pct });
      if (pct < 100) {
        setTimeout(tick, 90);
      } else {
        const sizeMB = (file.size / 1024 / 1024).toFixed(1);
        addDocument(deal.id, { name: file.name, tag, uploadedBy: deal.agentId, size: `${sizeMB} MB` });
        toast.success("Document uploaded", { description: `${file.name} · tagged ${tag}` });
        setProgress(null);
      }
    };
    setTimeout(tick, 120);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-5" data-testid="document-hub">
      {/* Operational Blocker Strip — derived in real-time from the
          deal's documents + activity + participants. Surfaces what is
          currently blocking the transaction with calm one-click follow-up. */}
      {deal?.id && <BlockerStrip dealId={deal.id} />}
      {/* Calm operational dependency strip — derived from the engine.
          Replaces the legacy hard-coded "missing" warning when backend
          dependencies are available. */}
      {deps?.blockers?.length ? (
        <div
          className="rounded-2xl border bg-white p-4"
          style={{
            borderColor: deps.summary.overdue > 0 ? "rgba(244,63,94,0.30)" : "#E2E8F0",
            boxShadow: "0 1px 0 rgba(15,23,42,0.02)",
          }}
          data-testid="docs-dependency-strip"
        >
          <div className="flex items-start gap-3 mb-2">
            <span
              className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
              style={{
                background: deps.summary.overdue > 0 ? "rgba(244,63,94,0.08)" : "rgba(245,158,11,0.10)",
                color: deps.summary.overdue > 0 ? "#9F1239" : "#92400E",
              }}
            >
              <AlertCircle size={14} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-bold text-slate-900">
                {deps.summary.blocking_exchange + deps.summary.blocking_completion} dependenc
                {(deps.summary.blocking_exchange + deps.summary.blocking_completion) === 1 ? "y" : "ies"} outstanding
              </p>
              <p className="text-[11.5px] text-slate-500 mt-0.5">
                {deps.summary.blocking_exchange ? `${deps.summary.blocking_exchange} blocking exchange` : ""}
                {deps.summary.blocking_exchange && deps.summary.blocking_completion ? " · " : ""}
                {deps.summary.blocking_completion ? `${deps.summary.blocking_completion} blocking completion` : ""}
                {deps.summary.overdue ? (
                  <span className="text-rose-700"> · {deps.summary.overdue} overdue</span>
                ) : null}
              </p>
            </div>
          </div>
          <ul className="space-y-1 mt-2 pt-2 border-t border-slate-100">
            {deps.blockers.slice(0, 5).map((b) => (
              <li
                key={b.key}
                className="flex items-center justify-between gap-3 text-[11.5px]"
                data-testid={`docs-blocker-${b.key}`}
              >
                <div className="min-w-0">
                  <span className="font-semibold text-slate-800">{b.label}</span>
                  <span className="text-slate-500"> · {b.responsible_label}</span>
                </div>
                <span
                  className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{
                    background: b.is_overdue
                      ? "rgba(244,63,94,0.10)"
                      : b.blocks_completion
                      ? "rgba(99,102,241,0.10)"
                      : "rgba(245,158,11,0.12)",
                    color: b.is_overdue
                      ? "#9F1239"
                      : b.blocks_completion
                      ? "#4338CA"
                      : "#92400E",
                  }}
                >
                  {b.is_overdue ? "Overdue" : b.blocks_completion ? "Completion" : "Exchange"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        // Legacy fallback — only when backend deps aren't loaded yet AND
        // the local chainStore detects gaps.
        missing.length > 0 && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-4 flex items-start gap-3" data-testid="docs-missing-warning">
            <FileText size={16} className="text-rose-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-[12.5px] font-bold text-rose-900">{missing.length} document{missing.length === 1 ? "" : "s"} still required</p>
              <p className="text-[11.5px] text-rose-800 mt-0.5">Outstanding: {missing.join(" · ")}</p>
            </div>
          </div>
        )
      )}

      {/* Folder rail */}
      <div className="flex items-center gap-1.5 overflow-x-auto cf-scroll pb-1" data-testid="docs-folder-rail">
        <button onClick={() => setFolder("All")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold border transition-all whitespace-nowrap ${folder === "All" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}>
          <FolderOpen size={11} /> All <span className="cf-num font-bold opacity-80">· {folderCounts.All}</span>
        </button>
        {folders.map((f) => (
          <button key={f} onClick={() => setFolder(f)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold border transition-all whitespace-nowrap ${folder === f ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`} data-testid={`docs-folder-${f.replace(/\s+/g, "-")}`}>
            <FolderOpen size={11} /> {f} <span className="cf-num font-bold opacity-80">· {folderCounts[f] || 0}</span>
          </button>
        ))}
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { if (!canUpload) return; e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={canUpload ? onDrop : (e) => e.preventDefault()}
        onClick={() => {
          if (!canUpload) {
            toast.message("Read-only access", {
              description: "Your access level on this deal doesn't include uploads.",
            });
            return;
          }
          inputRef.current?.click();
        }}
        className={`rounded-2xl p-8 text-center transition-all border-2 border-dashed ${
          !canUpload
            ? "border-slate-200 bg-slate-50/50 cursor-not-allowed opacity-70"
            : dragOver
              ? "border-indigo-500 bg-indigo-50 cursor-pointer"
              : "border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40 cursor-pointer"
        }`}
        data-testid="doc-dropzone"
        data-upload-locked={!canUpload}
        title={!canUpload ? "Read-only access" : undefined}
        aria-disabled={!canUpload}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          disabled={!canUpload}
          onChange={(e) => handleFiles(e.target.files)}
        />
        <div className={`w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-3 ${
          !canUpload
            ? "bg-slate-100 text-slate-400 border border-slate-200"
            : dragOver ? "bg-indigo-600 text-white" : "bg-indigo-50 text-indigo-600 border border-indigo-100"
        }`}>
          <CloudUpload size={22} />
        </div>
        <p className={`text-[14px] font-bold ${canUpload ? "text-slate-900" : "text-slate-500"}`}>
          {canUpload ? "Drag & drop a document here" : "Uploads are paused for your access level"}
        </p>
        <p className="text-[11.5px] text-slate-500 mt-1">
          {canUpload ? "PDF, DOCX, JPG · Max 50MB" : "Read-only access"}
        </p>

        <div className="mt-4 flex items-center justify-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mr-1">Tag as</span>
          {TAGS.map((t) => (
            <button
              key={t}
              onClick={(e) => { e.stopPropagation(); setTag(t); }}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border transition-all ${tag === t ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
              data-testid={`doc-tag-${t.replace(/\s+/g, "-")}`}
            >
              <Tag size={9} /> {t}
            </button>
          ))}
        </div>

        {progress && (
          <div className="mt-5 max-w-md mx-auto text-left" data-testid="doc-progress">
            <div className="flex items-center justify-between text-[11.5px] mb-1.5">
              <span className="text-slate-700 font-medium truncate">{progress.name}</span>
              <span className="cf-num text-indigo-700 font-bold">{Math.round(progress.pct)}%</span>
            </div>
            <div className="h-2 rounded-full bg-indigo-100 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-indigo-400 via-indigo-500 to-indigo-600 transition-all duration-200" style={{ width: `${progress.pct}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Document list */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="cf-eyebrow">Files in this deal · {filtered.length} of {docs.length}</p>
          <div className="ml-auto w-[220px]">
            <PremiumInput
              value={q}
              onChange={setQ}
              placeholder="Search files…"
              format="search"
              size="sm"
              clearable
              testid="doc-search"
            />
          </div>
          <div className="min-w-[140px]">
            <PremiumSelect
              value={filterTag}
              onChange={(v) => setFilterTag(v || "")}
              options={[
                { value: "", label: "All tags" },
                ...TAGS.map((t) => ({ value: t, label: t })),
              ]}
              size="sm"
              placeholder="All tags"
              clearable
              ariaLabel="Filter by tag"
              testid="doc-filter"
            />
          </div>
        </div>
        {docs.length === 0 && (
          <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 border-dashed" data-testid="doc-empty">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center mb-3">
              <FileText size={20} />
            </div>
            <p className="text-[14px] font-bold text-slate-900">No documents yet</p>
            <p className="text-[12px] text-slate-500 mt-1">Drag a file above or upload your first contract pack to get started.</p>
          </div>
        )}
        {docs.length > 0 && filtered.length === 0 && (
          <p className="text-[12px] text-slate-500 text-center py-6 bg-white rounded-2xl border border-slate-200 border-dashed">No files match — clear search or filter.</p>
        )}
        {filtered.map((d) => {
          const u = findContact(d.uploadedBy);
          const isFav = favDocs.has(d.id);
          return (
            <div key={d.id} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-200 hover:border-indigo-200 hover:shadow-[0_4px_16px_-8px_rgba(15,23,42,0.10)] transition-[box-shadow,border-color,transform] duration-200 hover:-translate-y-[0.5px]" data-testid={`doc-${d.id}`}>
              <div className="w-10 h-10 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">
                <FileText size={16} />
              </div>
              <div className="flex-1 min-w-0">
                {/* iter-395 · Filename → Status.  The filename now sits
                    on its own line so it never wraps behind the status
                    chip on narrow rails. */}
                <div className="flex items-center gap-2 min-w-0">
                  <p className="text-[13px] font-semibold text-slate-900 truncate tracking-[-0.005em]">{d.name}</p>
                  <span className={`shrink-0 inline-flex items-center h-[18px] px-1.5 rounded-md text-[9.5px] font-bold uppercase tracking-[0.10em] ring-1 ring-inset ${tagTone(d.tag)}`}>{d.tag}</span>
                  {d.version && d.version > 1 && <span className="shrink-0 inline-flex items-center h-[18px] px-1.5 rounded-md text-[9.5px] font-bold uppercase tracking-[0.10em] bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200 tabular-nums">v{d.version}</span>}
                </div>
                {/* iter-395 · Uploader → Time.  Uploader carries slightly
                    more weight than the timestamp so the eye reads
                    "who → when → where" left-to-right. */}
                <p className="text-[10.5px] text-slate-500 mt-1 truncate leading-snug">
                  <span className="text-slate-700 font-medium">{u?.name || "—"}</span>
                  <span className="text-slate-300 mx-1.5">·</span>
                  <span className="tabular-nums">{fmtAgo(d.at)}</span>
                  <span className="text-slate-300 mx-1.5">·</span>
                  <span className="tabular-nums">{d.size}</span>
                  {d.folder && (
                    <>
                      <span className="text-slate-300 mx-1.5">·</span>
                      <span>{d.folder}</span>
                    </>
                  )}
                </p>
              </div>
              <button onClick={() => toggleFavDoc(d.id)} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-150 ${isFav ? "text-amber-500 hover:bg-amber-50" : "text-slate-300 hover:bg-slate-100 hover:text-slate-600"}`} aria-label="Favourite" data-testid={`doc-fav-${d.id}`}>
                <Star size={13} fill={isFav ? "currentColor" : "none"} />
              </button>
              <button className="w-8 h-8 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 flex items-center justify-center transition-colors duration-150" aria-label="Download"><Download size={13} /></button>
              <button className="w-8 h-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-colors duration-150" aria-label="Remove"><Trash2 size={13} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
